import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fromEvent, map, tap } from 'rxjs';

import { LocalStorageService } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';

import { ThemeActions } from './theme.actions';
import { AppThemeMode } from './theme.model';
import { ThemeUtil } from './theme.plugin';
@Injectable({ providedIn: 'root' })
export class ThemeStateEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly store: Store = inject(Store);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly rootElement = document.querySelector('html');

  private LOCAL_STORAGE_KEY = 'terra-theme';

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.initGlobalState),
      map(() => {
        const savedMode = (this.localStorageService.get(this.LOCAL_STORAGE_KEY) as AppThemeMode) || 'auto';
        const systemMode = window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        this._setStatusBarColor(savedMode);
        // Initial terra-theme attribute on HTML tag is handled in index.html to avoid flashes of opposite modes
        return ThemeActions.initializeMode({
          theme: { appMode: savedMode, systemMode },
        });
      })
    )
  );

  systemModeChange$ = createEffect(() =>
    fromEvent<MediaQueryListEvent>(window.matchMedia('(prefers-color-scheme: dark)'), 'change').pipe(
      map(event => ThemeActions.setSystemMode({ mode: event.matches ? 'dark' : 'light' }))
    )
  );

  appModeChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ThemeActions.setAppMode),
        tap(({ mode }) => {
          this.localStorageService.set(this.LOCAL_STORAGE_KEY, mode);
          this.rootElement?.setAttribute(this.LOCAL_STORAGE_KEY, mode);
          this._setStatusBarColor(mode);
        })
      ),
    { dispatch: false }
  );

  private _setStatusBarColor(mode: AppThemeMode) {
    // Android needs to explicity set the statusbar and gesture bar color when the user has changed preferences
    if (Capacitor.isNativePlatform()) {
      const isAndroid = Capacitor.getPlatform() === 'android';
      if (mode === 'light') {
        StatusBar.setBackgroundColor({ color: 'ffffff' });
        StatusBar.setStyle({ style: Style.Light });
        if (isAndroid) {
          ThemeUtil.setGestureBarColor({ color: '#ffffff' });
        }
      } else if (mode === 'dark') {
        StatusBar.setBackgroundColor({ color: '262626' });
        StatusBar.setStyle({ style: Style.Dark });
        if (isAndroid) {
          ThemeUtil.setGestureBarColor({ color: '#262626' });
        }
      } else {
        // If the user switches to auto (which is to use system preference)
        // We need to see what their settings current are as
        // Ionic doesn't give a way to unset a color we've previously set
        const systemMode = window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        if (systemMode === 'light') {
          StatusBar.setBackgroundColor({ color: 'ffffff' });
          StatusBar.setStyle({ style: Style.Light });
          if (isAndroid) {
            ThemeUtil.setGestureBarColor({ color: '#ffffff' });
          }
        } else {
          StatusBar.setBackgroundColor({ color: '262626' });
          StatusBar.setStyle({ style: Style.Dark });
          if (isAndroid) {
            ThemeUtil.setGestureBarColor({ color: '#262626' });
          }
        }
      }
    }
  }
}
